// ============================================================================
// GsMoreMenuSheet — tiroir « Plus » du GsMobileBottomNav (mobile <md)
// ============================================================================
// UI_STANDARDS §17 : quatre onglets AU PLUS en barre basse. Le surplus vit
// derriere l'onglet « Plus », en BOTTOM SHEET (§4c « Surfaces superposees » :
// mobile = bottom sheet). Fermeture Echap / backdrop. Cibles tactiles >= 44px
// (min-h-11), touch-manipulation, dark-aware, zero hex.
//
// PROMU DE gest VERS LE SOCLE (V3, 2026-08-30). Il etait app-local depuis E1a
// sous la regle « on ne generalise pas sur un exemplaire ». Cette regle est
// tombee le 2026-08-28 : les cinq apps de gestion naissent avec une navigation
// mobile et depasseront toutes quatre destinations. La promotion n'est plus un
// declencheur conditionnel, c'est un acquis.
//
// EXTRACTION A PARITE STRICTE — le rendu de gest ne bouge pas d'un pixel. Un
// lot d'extraction qui corrige au passage ne prouve plus la parite. Les manques
// CONNUS sont donc laisses tels quels et inscrits au registre des echardes :
//   - pas de max-height / overflow-y : au-dela de ~8 items sur petit ecran, la
//     liste depasse le bas de l'ecran (invisible a 3 items, le cas de gest) ;
//   - pas de piege de focus : Echap et le backdrop ferment, mais la tabulation
//     sort du dialog.
//
// MEME MODELE D'ITEM, MEME REGLE QUE GsMobileBottomNav (feat-chrome-uniforme-
// du-parc, 2026-09-24) :
//   - un item `adminOnly` n'est jamais rendu — le tiroir est le surplus de la
//     barre basse, qui les filtre aussi ; la section admin vit dans GsSidebar ;
//   - un item `externalUrl` est un VRAI lien (<a target="_blank" rel="noopener
//     noreferrer">) : il sort de l'app sans passer par son routeur, et le
//     tiroir se ferme derriere lui. C'est ce qui rend le lanceur GS joignable
//     sur telephone.
//
// i18n PAR PROPS, defauts FR — JAMAIS de t() dans @gs/ui (UI_STANDARDS §6).

import { useEffect } from 'react';
import { ExternalLink, LogOut, X } from 'lucide-react';
import { GsIconButton } from './GsIconButton';
import type { SidebarItem, SidebarNavSection } from './GsSidebar';

/**
 * Sentinelle du 4e onglet « Plus » — valeur de `path` qui n'est PAS une
 * destination : elle ouvre le tiroir au lieu de naviguer.
 *
 * CONTRAT AVEC LE GATE ANTI-LIENS-MORTS (`tools/check-nav-routes.ts`) : cette
 * valeur ne doit correspondre a AUCUNE `<Route>`, donc le gate doit l'ignorer.
 * Il le fait via `isNavSentinelPath()`, qui reconnait explicitement le prefixe
 * `#`. Avant V3 l'exemption n'existait que par EFFET DE BORD d'un filtre
 * commente « faux positifs evidents » : un resserrement de ce filtre aurait
 * casse la navigation de gest sans que personne fasse le lien.
 *
 * Toute app qui monte un tiroir « Plus » utilise CETTE constante — jamais un
 * litteral '#plus' recopie, sinon le contrat se re-disperse.
 */
export const GS_MORE_PATH = '#plus';

export interface GsMoreMenuSheetProps {
  open: boolean;
  onClose: () => void;
  /** Meme modele d'item que GsSidebar et GsMobileBottomNav (source unique). */
  items: SidebarItem[];
  /** Sections metier (intertitres), meme forme que GsSidebar.sections. */
  sections?: SidebarNavSection[];
  onNavigate: (path: string) => void;
  /** Titre du tiroir (i18n par prop — defaut FR). */
  title?: string;
  /** Libelle a11y du bouton fermer (i18n par prop — defaut FR). */
  closeLabel?: string;
  /** Deconnexion mobile — rendue seulement si fournie (i18n par logoutLabel). */
  onLogout?: () => void;
  /** Libelle de l entree de deconnexion (defaut aligne GsUserMenu). */
  logoutLabel?: string;
}

const sectionTitleClass =
  'px-3 pt-3 pb-1 text-chip font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500';

export function GsMoreMenuSheet({
  open,
  onClose,
  items,
  sections,
  onNavigate,
  title = 'Plus',
  closeLabel = 'Fermer',
  onLogout,
  logoutLabel = 'Se déconnecter',
}: GsMoreMenuSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const itemClass =
    'flex min-h-11 w-full touch-manipulation items-center gap-3 rounded-lg px-3 py-2.5 text-left text-body text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800';

  const renderNavItem = (item: SidebarItem) => {
    const { key, label, icon: Icon, path, badge, externalUrl } = item;
    const content = (
      <>
        <Icon size={18} className="shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {externalUrl === true && (
          <ExternalLink size={14} className="shrink-0 text-slate-400 dark:text-slate-500" aria-hidden />
        )}
        {badge != null && badge !== 0 && badge !== '' && (
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-chip font-semibold tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-200">
            {badge}
          </span>
        )}
      </>
    );
    if (externalUrl === true) {
      return (
        <a
          key={key}
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className={`${itemClass} no-underline`}
        >
          {content}
        </a>
      );
    }
    return (
      <button
        key={key}
        type="button"
        onClick={() => { onClose(); onNavigate(path); }}
        className={itemClass}
      >
        {content}
      </button>
    );
  };

  const renderItemList = (list: SidebarItem[]) =>
    list.filter((item) => !item.adminOnly).map((item) => renderNavItem(item));

  const hasSections = sections && sections.some((s) => s.items.length > 0);

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 dark:bg-black/60"
      />
      <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-slate-200 bg-white p-3 pb-6 shadow-md dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-1 flex items-center justify-between px-1">
          <h2 className="text-field font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
          <GsIconButton
            icon={<X size={18} aria-hidden />}
            tooltip={closeLabel}
            variant="ghost"
            size="lg"
            aria-label={closeLabel}
            onClick={onClose}
            className="min-h-11 min-w-11 touch-manipulation"
          />
        </div>
        <nav>
          {hasSections
            ? sections!.map((section) =>
                section.items.length > 0 ? (
                  <div key={section.key}>
                    <div className={sectionTitleClass}>{section.label}</div>
                    {renderItemList(section.items)}
                  </div>
                ) : null,
              )
            : renderItemList(items)}
        </nav>
        {onLogout && (
          <div className="mt-2 border-t border-slate-200 pt-2 dark:border-slate-700">
            <button
              type="button"
              onClick={() => { onClose(); onLogout(); }}
              className={`${itemClass} text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30`}
            >
              <LogOut size={18} className="shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{logoutLabel}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
